import type { AuthProviderId, CrewMember, DeskAuthSettings, DeskRole } from "./types";
import { crewById, patchCrewMember } from "./crew";

/** Desk sign-on stays local Company/Crew PIN. */
export const DESK_AUTH_PROVIDER: AuthProviderId = "local-crew";
/** Nanode email/password owns the career save — not Dispatcher/Pilot. */
export const ACCOUNT_AUTH_PROVIDER: AuthProviderId = "nanode";
/** Wave A3 desk gate. Nanode is the account layer beside this, not a replacement. */
export const ACTIVE_AUTH_PROVIDER: AuthProviderId = DESK_AUTH_PROVIDER;

export const AUTH_PIN_SCHEME = "ns.dispatch.pin.v1";

export const DEFAULT_IDLE_TIMEOUT_MIN = 20;
export const MIN_IDLE_TIMEOUT_MIN = 5;
export const MAX_IDLE_TIMEOUT_MIN = 60;
export const IDLE_TIMEOUT_CHOICES = [5, 10, 15, 20, 30, 45, 60] as const;

export const MIN_PIN_LEN = 4;
export const MAX_PIN_LEN = 32;

export const DEFAULT_DESK_AUTH: DeskAuthSettings = {
  idleTimeoutMin: DEFAULT_IDLE_TIMEOUT_MIN,
  requirePinOnRoleSwitch: true,
};

export type DeskPinRecord = {
  pinSalt: string;
  pinHash: string;
};

export type DeskSessionRecord = {
  crewId: string;
  role: DeskRole;
  startedAtMs: number;
  lastActivityAtMs: number;
  lastHiddenAtMs: number | null;
};

export function hydrateDeskAuth(raw?: Partial<DeskAuthSettings> | null): DeskAuthSettings {
  const n = Number(raw?.idleTimeoutMin);
  return {
    idleTimeoutMin: clampIdleTimeoutMin(Number.isFinite(n) ? n : DEFAULT_IDLE_TIMEOUT_MIN),
    requirePinOnRoleSwitch: raw?.requirePinOnRoleSwitch !== false,
  };
}

export function oppositeDeskRole(role: DeskRole): DeskRole {
  return role === "dispatcher" ? "pilot" : "dispatcher";
}

export type RoleSwitchPlan =
  | {
      ok: true;
      kind: "pin" | "seamless";
      crewId: string;
      from: DeskRole;
      to: DeskRole;
    }
  | { ok: false; error: string };

/**
 * Same crew, other seat. PIN (default) or loading-screen only.
 * Does not touch the Nanode account session or the company board.
 */
export function planDeskRoleSwitch(input: {
  crewId: string | null | undefined;
  roles: readonly DeskRole[] | null | undefined;
  currentRole: DeskRole;
  requirePinOnRoleSwitch?: boolean;
}): RoleSwitchPlan {
  const crewId = input.crewId?.trim() ?? "";
  if (!crewId) return { ok: false, error: "Sign on before switching Dispatch and Pilot." };
  const to = oppositeDeskRole(input.currentRole);
  const roles = input.roles ?? [];
  if (!roles.includes(to)) {
    return {
      ok: false,
      error:
        to === "pilot"
          ? "This crew account is not signed for Pilot."
          : "This crew account is not signed for Dispatcher.",
    };
  }
  const requirePin = input.requirePinOnRoleSwitch !== false;
  return {
    ok: true,
    kind: requirePin ? "pin" : "seamless",
    crewId,
    from: input.currentRole,
    to,
  };
}

export function clampIdleTimeoutMin(min: number): number {
  const rounded = Math.round(min);
  if (!Number.isFinite(rounded)) return DEFAULT_IDLE_TIMEOUT_MIN;
  return Math.min(MAX_IDLE_TIMEOUT_MIN, Math.max(MIN_IDLE_TIMEOUT_MIN, rounded));
}

export function idleTimeoutMs(settings: DeskAuthSettings): number {
  return clampIdleTimeoutMin(settings.idleTimeoutMin) * 60_000;
}

export function crewHasPin(member: Pick<CrewMember, "pinSalt" | "pinHash"> | null | undefined): boolean {
  return Boolean(member?.pinSalt?.trim() && member?.pinHash?.trim());
}

export function crewCanTakeRole(member: Pick<CrewMember, "roles">, role: DeskRole): boolean {
  return member.roles.includes(role);
}

export function validateDeskPin(raw: string): { ok: true; pin: string } | { ok: false; error: string } {
  const pin = raw.trim();
  if (!pin) return { ok: false, error: "Enter a desk PIN." };
  if (/\s/.test(raw)) return { ok: false, error: "Desk PIN cannot contain spaces." };
  if (pin.length < MIN_PIN_LEN) return { ok: false, error: `Desk PIN must be at least ${MIN_PIN_LEN} characters.` };
  if (pin.length > MAX_PIN_LEN) return { ok: false, error: `Desk PIN must be at most ${MAX_PIN_LEN} characters.` };
  return { ok: true, pin };
}

export function bytesToHex(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let out = "";
  for (let i = 0; i < view.length; i += 1) {
    out += view[i]!.toString(16).padStart(2, "0");
  }
  return out;
}

export function hexEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diff === 0;
}

export function newPinSalt(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return bytesToHex(bytes);
}

export async function hashDeskPin(pin: string, salt: string): Promise<string> {
  const data = new TextEncoder().encode(`${AUTH_PIN_SCHEME}\n${salt}\n${pin}`);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return bytesToHex(digest);
}

export async function makeDeskPin(
  raw: string,
): Promise<({ ok: true } & DeskPinRecord) | { ok: false; error: string }> {
  const checked = validateDeskPin(raw);
  if (!checked.ok) return checked;
  const pinSalt = newPinSalt();
  const pinHash = await hashDeskPin(checked.pin, pinSalt);
  return { ok: true, pinSalt, pinHash };
}

export async function verifyDeskPin(raw: string, record: DeskPinRecord): Promise<boolean> {
  if (!record.pinSalt || !record.pinHash) return false;
  const checked = validateDeskPin(raw);
  if (!checked.ok) return false;
  const got = await hashDeskPin(checked.pin, record.pinSalt);
  return hexEqual(got, record.pinHash);
}

export function withCrewPin(member: CrewMember, record: DeskPinRecord): CrewMember {
  return { ...member, pinSalt: record.pinSalt, pinHash: record.pinHash };
}

export function mergeCrewPins(incoming: CrewMember[], local: CrewMember[]): CrewMember[] {
  return incoming.map((member) => {
    if (crewHasPin(member)) return member;
    const prior = local.find((c) => c.id === member.id);
    if (prior && crewHasPin(prior)) {
      return { ...member, pinSalt: prior.pinSalt, pinHash: prior.pinHash };
    }
    return member;
  });
}

export type AuthenticateDeskInput = {
  roster: CrewMember[];
  crewId: string;
  pin: string;
  role: DeskRole;
};

export type AuthenticateDeskResult =
  | { ok: true; member: CrewMember; role: DeskRole }
  | { ok: false; error: string };

export async function authenticateDesk(input: AuthenticateDeskInput): Promise<AuthenticateDeskResult> {
  const member = crewById(input.roster, input.crewId);
  if (!member) return { ok: false, error: "That crew member is not on this company roster." };
  if (!crewCanTakeRole(member, input.role)) {
    return {
      ok: false,
      error:
        input.role === "dispatcher"
          ? "This crew member is not signed for Dispatcher."
          : "This crew member is not signed for Pilot.",
    };
  }
  if (!crewHasPin(member)) {
    return { ok: false, error: "This crew account has no desk PIN yet. Set one to lock the board." };
  }
  if (!input.pin.trim()) return { ok: false, error: "Enter a desk PIN." };
  const ok = await verifyDeskPin(input.pin, { pinSalt: member.pinSalt, pinHash: member.pinHash });
  if (!ok) return { ok: false, error: "Desk PIN does not match." };
  return { ok: true, member, role: input.role };
}

export type ClaimDeskPinInput = {
  roster: CrewMember[];
  crewId: string;
  newPin: string;
  confirmPin: string;
  role: DeskRole;
};

export type ClaimDeskPinResult =
  | { ok: true; member: CrewMember; roster: CrewMember[]; role: DeskRole }
  | { ok: false; error: string };

export async function claimDeskPin(input: ClaimDeskPinInput): Promise<ClaimDeskPinResult> {
  const member = crewById(input.roster, input.crewId);
  if (!member) return { ok: false, error: "That crew member is not on this company roster." };
  if (crewHasPin(member)) {
    return { ok: false, error: "This crew account already has a desk PIN. Sign on with it." };
  }
  if (!crewCanTakeRole(member, input.role)) {
    return { ok: false, error: "This crew member cannot take that role." };
  }
  if (input.newPin !== input.confirmPin) {
    return { ok: false, error: "PIN and confirmation do not match." };
  }
  const made = await makeDeskPin(input.newPin);
  if (!made.ok) return made;
  const next = withCrewPin(member, made);
  return {
    ok: true,
    member: next,
    roster: patchCrewMember(input.roster, member.id, { pinSalt: next.pinSalt, pinHash: next.pinHash }),
    role: input.role,
  };
}

export function sessionIdleExpired(session: DeskSessionRecord, settings: DeskAuthSettings, nowMs: number): boolean {
  return nowMs - session.lastActivityAtMs >= idleTimeoutMs(settings);
}

export function sessionClosedExpired(session: DeskSessionRecord, settings: DeskAuthSettings, nowMs: number): boolean {
  if (session.lastHiddenAtMs == null) return false;
  return nowMs - session.lastHiddenAtMs >= idleTimeoutMs(settings);
}

/** Live if last activity and last time the app was backgrounded are both inside the idle window. */
export function sessionIsLive(
  session: DeskSessionRecord | null | undefined,
  settings: DeskAuthSettings,
  nowMs = Date.now(),
): boolean {
  if (!session?.crewId) return false;
  if (session.role !== "dispatcher" && session.role !== "pilot") return false;
  if (sessionIdleExpired(session, settings, nowMs)) return false;
  if (sessionClosedExpired(session, settings, nowMs)) return false;
  return true;
}

export function touchSession(
  session: DeskSessionRecord,
  nowMs = Date.now(),
  patch: Partial<Pick<DeskSessionRecord, "lastHiddenAtMs">> = {},
): DeskSessionRecord {
  return {
    ...session,
    lastActivityAtMs: nowMs,
    ...patch,
  };
}

/** Same crew sitting. Role changes, activity is touched, startedAtMs stays. Does not write storage. */
export function switchDeskSessionRole(
  session: DeskSessionRecord,
  role: DeskRole,
  nowMs = Date.now(),
): DeskSessionRecord {
  return {
    ...session,
    role,
    lastActivityAtMs: nowMs,
    lastHiddenAtMs: null,
  };
}

export function openDeskSession(input: { crewId: string; role: DeskRole; nowMs?: number }): DeskSessionRecord {
  const nowMs = input.nowMs ?? Date.now();
  return {
    crewId: input.crewId,
    role: input.role,
    startedAtMs: nowMs,
    lastActivityAtMs: nowMs,
    lastHiddenAtMs: null,
  };
}
